import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, CalendarClock, MapPin, Upload, FileText, X, ShieldAlert, AlertTriangle, PhoneCall, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { careersApi } from "../../services/careersApi";
import { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { useLocalization } from "../../hooks/useLocalization";
import { PublicHeader } from "../../components/PublicHeader";

export default function CareerApplicationStatusPage() {
  const { applicantId } = useParams();
  const { t, language } = useLocalization();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("10:00");
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await careersApi.getApplicantStatus(applicantId);
    setStatus(data);
  };

  useEffect(() => {
    load().catch(() => setError(t("careers.applicationNotFound")));
  }, [applicantId]);

  const handleReschedule = async () => {
    if (!rescheduleDate) { toast.error(t("careers.chooseNewDateTime")); return; }
    try {
      await careersApi.rescheduleBooking(applicantId, { date: rescheduleDate, start_time: rescheduleTime });
      toast.success(t("careers.interviewRescheduledToast"));
      setRescheduling(false);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleCancelBooking = async () => {
    try {
      await careersApi.cancelBooking(applicantId, "Cancelled by applicant");
      toast.success(t("careers.interviewCancelledToast"));
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleUploadResume = async () => {
    if (!resumeFile) { toast.error(t("careers.chooseResumeFileFirst")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", resumeFile);
      await careersApi.uploadResume(applicantId, fd);
      toast.success(t("careers.resumeUploadedToast"));
      setResumeFile(null);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const handleRemoveResume = async () => {
    try { await careersApi.removeResume(applicantId); toast.success(t("careers.resumeRemovedToast")); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (error && !status) {
    return (
      <div className="min-h-screen bg-white text-blue-950" data-testid="career-status-not-found">
        <PublicHeader />
        <div className="flex flex-col items-center justify-center gap-4 py-24">
          <p className="text-gray-500">{error}</p>
          <Link to="/careers" className="text-amber-500 font-semibold hover:underline">{t("careers.backToJobs")}</Link>
        </div>
      </div>
    );
  }
  if (!status) return <div className="min-h-screen bg-white" data-testid="career-status-loading" />;

  const jobTitle = (language === "ja" ? status.job_title_ja : status.job_title_en) || status.job_title_en;
  const isTerminal = ["HIRED", "REJECTED", "WITHDRAWN"].includes(status.status);
  const booking = status.interview_booking;
  const isContactMe = booking?.interview_mode === "CONTACT_ME";
  const canManageBooking = booking && ["PENDING_CONFIRMATION", "CONFIRMED"].includes(booking.status);

  return (
    <div className="min-h-screen bg-white text-blue-950" data-testid="career-application-status-page">
      <PublicHeader />
      <header className="max-w-lg mx-auto px-6 pt-8 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle2 size={28} className="text-amber-500" />
          <h1 className="text-2xl font-extrabold tracking-tight text-blue-950" data-testid="career-application-received-title">{t("careers.applicationReceived")}</h1>
        </div>
        <p className="text-gray-500 text-sm mb-6" data-testid="career-application-received-subtitle">
          {t("careers.applicationReceivedSubtitle").replace("{name}", status.legal_name)}
        </p>

        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 mb-6">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-1">{t("careers.yourStatus")}</p>
          <p className="text-sm text-gray-600 mb-1">{t("careers.appliedFor")}: <span className="font-semibold text-blue-950">{jobTitle}</span></p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mb-3"><KeyRound size={11} /> {t("careers.confirmationCode")}: {status.id.slice(0, 8).toUpperCase()}</p>
          <StatusBadge status={status.status} />
        </div>

        {(status.work_authorization_review_required || status.availability_review_required) && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 space-y-2">
            {status.work_authorization_review_required && (
              <p className="text-xs text-amber-700 flex items-start gap-2"><ShieldAlert size={14} className="shrink-0 mt-0.5" /> {t("careers.workAuthReviewFollowUp")}</p>
            )}
            {status.availability_review_required && (
              <p className="text-xs text-amber-700 flex items-start gap-2"><AlertTriangle size={14} className="shrink-0 mt-0.5" /> {t("careers.availabilityReviewFollowUp")}</p>
            )}
          </div>
        )}

        {isContactMe && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 mb-6" data-testid="career-contact-me-card">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-2 flex items-center gap-1.5"><PhoneCall size={13} /> {t("careers.interviewSchedulingTitle")}</p>
            <p className="text-sm text-gray-600">{t("careers.contactMeMessage")}</p>
          </div>
        )}

        {booking && !isContactMe && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 mb-6" data-testid="career-booked-interview-card">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-2 flex items-center gap-1.5"><CalendarClock size={13} /> {t("careers.slotBooked")}</p>
            <p className="text-sm text-gray-700">{booking.date} @ {booking.start_time} JST</p>
            <p className="text-xs text-gray-500 mt-1">{t("careers.interviewBookedHelperText")}</p>
            <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-1"><MapPin size={13} /> {t("careers.locationYokosuka")}</p>
            <p className="text-[11px] text-gray-500 mt-2">{t("careers.whatToBring")}</p>
            <p className="text-xs font-semibold mt-3 text-amber-600" data-testid="interview-reminder-message">
              {booking.status === "CONFIRMED" ? t("careers.interviewConfirmedMessage") : t("careers.interviewPendingMessage")}
            </p>

            {canManageBooking && !rescheduling && (
              <div className="flex gap-2 mt-3">
                <button data-testid="reschedule-interview-btn" onClick={() => { setRescheduleDate(booking.date); setRescheduleTime(booking.start_time); setRescheduling(true); }} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-blue-950">{t("careers.rescheduleButton")}</button>
                <button data-testid="cancel-interview-btn" onClick={handleCancelBooking} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-blue-950 flex items-center gap-1"><X size={12} /> {t("careers.cancelButton")}</button>
              </div>
            )}
            {rescheduling && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <input data-testid="reschedule-date-input" type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-blue-950" />
                  <input data-testid="reschedule-time-input" type="time" step={1800} value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-blue-950" />
                </div>
                <button data-testid="confirm-reschedule-btn" onClick={handleReschedule} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-400 text-blue-950 hover:bg-amber-300">{t("careers.confirmNewTimeButton")}</button>
              </div>
            )}
          </div>
        )}

        {!isTerminal && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 mb-6" data-testid="career-resume-card">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-2 flex items-center gap-1.5"><FileText size={13} /> {t("careers.resumeSectionTitle")}</p>
            {status.resume_filename ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700" data-testid="career-resume-filename">{status.resume_filename}</p>
                <button data-testid="remove-resume-btn" onClick={handleRemoveResume} className="text-xs font-semibold text-red-500 hover:underline">{t("careers.removeButton")}</button>
              </div>
            ) : (
              <p className="text-xs text-gray-500 mb-2" data-testid="career-no-resume">{t("careers.noResumeUploaded")}</p>
            )}
            <div className="flex items-center gap-2 mt-3">
              <input data-testid="resume-file-input" type="file" accept=".pdf,.docx" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} className="text-xs text-gray-600 flex-1" />
              <button data-testid="upload-resume-btn" disabled={uploading} onClick={handleUploadResume} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-400 text-blue-950 hover:bg-amber-300 disabled:opacity-50 flex items-center gap-1">
                <Upload size={12} /> {status.resume_filename ? t("careers.replaceButton") : t("careers.uploadButton")}
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">{t("careers.resumeFileNote")}</p>
          </div>
        )}
      </header>
    </div>
  );
}

